import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { API_URL } from '../../config';
import { SAVE_PROPERTY_INSTRUCTIONAL_VIDEOS } from '../../graphql/queries';

const MAX_VIDEO_SIZE_BYTES = 500 * 1024 * 1024;
const AUTO_SAVE_DELAY_MS = 1500;

function VideoThumbnail({ videoUrl }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handleTogglePlay = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="relative group rounded-lg overflow-hidden bg-black aspect-video">
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full h-full object-contain"
        preload="metadata"
        playsInline
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />
      <button
        type="button"
        onClick={handleTogglePlay}
        className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors"
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
        tabIndex={0}
      >
        {!isPlaying && (
          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <svg className="w-5 h-5 text-gray-800 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
            </svg>
          </div>
        )}
      </button>
    </div>
  );
}

function InstructionalVideoItem({
  video,
  index,
  totalCount,
  uploadingIndex,
  uploadProgress,
  onFieldChange,
  onRemove,
  onUpload,
  onReorderStart,
  onReorderDrop
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/webm': ['.webm']
    },
    maxFiles: 1,
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return;
      onUpload(acceptedFiles[0], index);
    }
  });

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const hasVideo = !!video.videoUrl;

  return (
    <div
      className="border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow"
      draggable
      onDragStart={() => onReorderStart(index)}
      onDragOver={handleDragOver}
      onDrop={() => onReorderDrop(index)}
    >
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
        <div className="flex items-center gap-2">
          <span className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600" title="Drag to reorder">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
            </svg>
          </span>
          <span className="text-xs font-medium text-gray-500">
            Video {index + 1} of {totalCount}
          </span>
        </div>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
          aria-label={`Remove video ${index + 1}`}
        >
          Remove
        </button>
      </div>

      <div className="p-4">
        <div className={`flex gap-4 ${hasVideo ? 'flex-col md:flex-row' : 'flex-col'}`}>
          {hasVideo && (
            <div className="w-full md:w-64 flex-shrink-0">
              <VideoThumbnail videoUrl={video.videoUrl} />
              <div {...getRootProps()} className="mt-2">
                <input {...getInputProps()} />
                <button
                  type="button"
                  className="w-full text-xs text-blue-600 hover:text-blue-800 font-medium py-1 transition-colors"
                >
                  Replace video
                </button>
              </div>
            </div>
          )}

          <div className="flex-1 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={video.title || ''}
                placeholder="e.g. How to access the property"
                onChange={(event) => onFieldChange(index, 'title', event.target.value)}
                className="px-3 py-2 h-10 w-full border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-400 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-sm transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
              <textarea
                rows="2"
                value={video.description || ''}
                placeholder="Brief description of what this video covers"
                onChange={(event) => onFieldChange(index, 'description', event.target.value)}
                className="px-3 py-2 w-full border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-400 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-sm transition-colors resize-none"
              />
            </div>

            {!hasVideo && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Video</label>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input {...getInputProps()} />
                  <div className="space-y-2">
                    <svg
                      className="mx-auto h-10 w-10 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <div className="text-sm text-gray-600">
                      <span className="text-blue-600 font-medium hover:text-blue-500">Click to upload</span>{' '}
                      or drag and drop
                    </div>
                    <p className="text-xs text-gray-500">MP4, MOV, or WebM up to 500MB</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {uploadingIndex === index && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function GuestInstructionalVideosSection({ propertyId, instructionalVideos = [], onChange }) {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragIndex, setDragIndex] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const autoSaveTimerRef = useRef(null);
  const videosRef = useRef(instructionalVideos);

  const [saveVideosMutation] = useMutation(SAVE_PROPERTY_INSTRUCTIONAL_VIDEOS);

  useEffect(() => {
    videosRef.current = instructionalVideos;
  }, [instructionalVideos]);

  const persistVideos = useCallback(async (videos) => {
    if (!propertyId) return;

    const saveable = videos.filter(
      (v) => v.title && v.title.trim() !== '' && v.videoUrl && v.videoUrl.trim() !== ''
    );

    if (saveable.length === 0 && videos.length > 0) return;

    setSaveStatus('saving');
    try {
      const result = await saveVideosMutation({
        variables: {
          propertyId,
          videos: saveable.map((v, i) => ({
            title: v.title.trim(),
            description: v.description ? v.description.trim() : null,
            videoUrl: v.videoUrl.trim(),
            sortOrder: i,
          })),
        },
        errorPolicy: 'none',
      });

      if (result.errors?.length) {
        const msg = result.errors.map((e) => e.message).join(', ');
        throw new Error(msg);
      }

      const data = result.data;
      if (data?.updatePropertyInstructionalVideos?.instructionalVideos) {
        const saved = data.updatePropertyInstructionalVideos.instructionalVideos;
        const merged = videos.map((local) => {
          const match = saved.find(
            (s) => s.videoUrl === local.videoUrl?.trim() && s.title === local.title?.trim()
          );
          return match ? { ...local, id: match.id } : local;
        });
        onChange?.({ instructionalVideos: merged });
      }

      setSaveStatus('saved');
      toast.success('Videos saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving instructional videos:', error);
      setSaveStatus('error');
      toast.error(`Failed to save videos: ${error.message || 'Please try again.'}`);
    }
  }, [propertyId, saveVideosMutation, onChange]);

  const scheduleAutoSave = useCallback((videos) => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      persistVideos(videos);
    }, AUTO_SAVE_DELAY_MS);
  }, [persistVideos]);

  const handleUpdateVideos = (nextVideos) => {
    onChange?.({ instructionalVideos: nextVideos });
    scheduleAutoSave(nextVideos);
  };

  const handleAddVideo = () => {
    const nextVideos = [
      ...instructionalVideos,
      {
        id: `temp-${Date.now()}-${Math.random()}`,
        title: '',
        description: '',
        videoUrl: ''
      }
    ];
    onChange?.({ instructionalVideos: nextVideos });
  };

  const handleRemoveVideo = (indexToRemove) => {
    const nextVideos = instructionalVideos.filter((_, index) => index !== indexToRemove);
    onChange?.({ instructionalVideos: nextVideos });
    persistVideos(nextVideos);
  };

  const handleFieldChange = (index, field, value) => {
    const nextVideos = instructionalVideos.map((video, i) =>
      i === index ? { ...video, [field]: value } : video
    );
    handleUpdateVideos(nextVideos);
  };

  const handleUploadVideo = async (file, index) => {
    if (!file) return;

    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      toast.error('Video is too large. Maximum size is 500MB.');
      return;
    }

    setUploadingIndex(index);
    setUploadProgress(0);

    try {
      setUploadProgress(5);

      const response = await fetch(`${API_URL}/api/properties/get-video-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ fileName: file.name, fileType: file.type })
      });

      if (!response.ok) {
        throw new Error(`Failed to get upload URL: ${response.statusText}`);
      }

      const { uploadUrl, videoUrl, headers } = await response.json();
      setUploadProgress(20);

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers,
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload video: ${uploadResponse.statusText}`);
      }

      setUploadProgress(90);

      const currentVideos = videosRef.current;
      const nextVideos = currentVideos.map((video, i) =>
        i === index ? { ...video, videoUrl } : video
      );
      onChange?.({ instructionalVideos: nextVideos });

      setUploadProgress(100);
      toast.success('Video uploaded successfully');

      await persistVideos(nextVideos);
    } catch (error) {
      console.error('Error uploading instructional video:', error);
      toast.error('Failed to upload video. Please try again.');
      setUploadProgress(0);
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleReorderStart = (index) => {
    setDragIndex(index);
  };

  const handleReorderDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return;
    const nextVideos = [...instructionalVideos];
    const [moved] = nextVideos.splice(dragIndex, 1);
    nextVideos.splice(index, 0, moved);
    setDragIndex(null);
    handleUpdateVideos(nextVideos);
  };

  const handleManualSave = () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    persistVideos(instructionalVideos);
  };

  const saveIndicator = (() => {
    if (saveStatus === 'saving') return { text: 'Saving...', color: 'text-blue-600' };
    if (saveStatus === 'saved') return { text: 'Saved', color: 'text-green-600' };
    if (saveStatus === 'error') return { text: 'Save failed', color: 'text-red-600' };
    return null;
  })();

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">Guest Instructional Videos</h3>
          <p className="mt-1 text-sm text-gray-500">
            Upload short how-to videos for guests (for example, how to access the property).
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Videos auto-save after upload. Maximum file size: 500MB per video.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saveIndicator && (
            <span className={`text-xs font-medium ${saveIndicator.color}`}>
              {saveIndicator.text}
            </span>
          )}
          <button
            type="button"
            onClick={handleManualSave}
            disabled={saveStatus === 'saving'}
            className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
          >
            Save Videos
          </button>
          <button
            type="button"
            onClick={handleAddVideo}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Add Video
          </button>
        </div>
      </div>

      {instructionalVideos.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <p className="mt-2 text-sm text-gray-500">
            No guest instructional videos have been added yet.
          </p>
          <button
            type="button"
            onClick={handleAddVideo}
            className="mt-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Add Your First Video
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {instructionalVideos.map((video, index) => (
            <InstructionalVideoItem
              key={video.id || index}
              video={video}
              index={index}
              totalCount={instructionalVideos.length}
              uploadingIndex={uploadingIndex}
              uploadProgress={uploadProgress}
              onFieldChange={handleFieldChange}
              onRemove={handleRemoveVideo}
              onUpload={handleUploadVideo}
              onReorderStart={handleReorderStart}
              onReorderDrop={handleReorderDrop}
            />
          ))}
        </div>
      )}
    </div>
  );
}
